import type { FunctionComponent } from 'react'

import { FlowPage } from '@monthio/design-system'
import { getCurrency, trackingService } from '@monthio/react-components'
import { useFormik } from 'formik'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { DebtItem } from './Components/debtItem'
import type {
	DeleteDebtItem,
	UpdateDebtItem,
} from './Components/DebtOverviewCard'
import type { DebtPayment } from './Components/DebtOverviewCard/DebtOverviewCard'

import { Conditionally } from '../../modulesTemp/shared/conditionally'
import { flowContainerWithFixedButtonsDirectParentCssHack } from '../../modulesTemp/shared/slider'
import { canonicalDebtRecord, isValidItem } from './Components/debtItem'
import { DebtOverviewCard } from './Components/DebtOverviewCard'
import { DebtOverviewIntro } from './Components/DebtOverviewIntro'
import { getMixPanelStatistics } from './Components/debtOverviewRevampedMixpanel.utils'
import { DebtSummary } from './Components/DebtSummary'
import { generateGuid } from './Components/generateGuid'
import { transformDebtData } from './Components/model/debtDataTransformation'
import { OverviewIllustration } from './Components/OverviewIllustration'
import { DebtRecordRevampedType } from './types/debtOverviewApi.types'
import { useDebtOverviewModule } from './useDebtOverviewModule'

const introPage = -1
const transitionDurationMs = 300

const Form = styled.form`
	min-height: 100%;
	${flowContainerWithFixedButtonsDirectParentCssHack}
`

const useDebtOverviewTitle = (
	currentPage: number,
	totalPages: number,
	navigatedFromSummary: boolean,
	clientName?: string,
	isCreating?: boolean,
): { title: string; subTitle: string; primaryButtonTitle: string } => {
	const { t } = useTranslation()

	if (isCreating) {
		return {
			title: t('debtOverview-header-add-title'),
			subTitle: t('debtOverview-add-subtitle'),
			primaryButtonTitle: t('debtOverview-button-add'),
		}
	}

	switch (true) {
		case currentPage === introPage:
			return {
				title: t('debtOverview-header-intro-title'),
				subTitle: t('debtOverview-intro-subtitle'),
				primaryButtonTitle: t('debtOverview-intro-button-start'),
			}
		case totalPages === 0:
			return {
				title: t('debtOverview-header-no-debt-title'),
				subTitle: t('debtOverview-no-debt-subtitle'),
				primaryButtonTitle: t('debtOverview-button-next'), // t('debtOverview-intro-button-start'),
			}
		case currentPage < totalPages:
			return {
				title: t('debtOverview-header-progress-title', {
					currentPage: currentPage + 1,
					totalPages,
				}),
				subTitle: t('debtOverview-progress-subtitle'),
				primaryButtonTitle: navigatedFromSummary
					? t('debtOverview-button-goto-summary')
					: t('debtOverview-button-next'),
			}
		default:
			return {
				title: t('debtOverview-header-summary-title'),
				subTitle: t('debtOverview-summary-subtitle', {
					clientName:
						clientName || t('debtOverview-summary-client-name-fallback'),
				}),
				primaryButtonTitle: t('debtOverview-button-next'),
			}
	}
}

export const DebtOverview: FunctionComponent = () => {
	const { data, finish, config } = useDebtOverviewModule()

	const { debtItems: debtEntries, debtPayments } = useMemo(
		() =>
			transformDebtData({
				debtRecords: data.debtRecords,
				debtPayments: data.debtPayments,
				allDebtPayments: data.allDebtPayments,
				creditors: data.creditors,
			}),
		[data.debtRecords, data.debtPayments, data.allDebtPayments, data.creditors],
	)

	const [suggestedDebtPayments, setSuggestedDebtPayments] = useState<
		DebtPayment[]
	>(debtPayments.map((payment) => ({ ...payment, debtId: undefined })))

	const hasIntroPage = debtEntries.length > 0

	const [currentPage, setCurrentPage] = useState(hasIntroPage ? introPage : 0)
	const [navigatedFromSummary, setNavigatedFromSummary] = useState(false)
	const currency = getCurrency(config.regionId)

	const { setFieldValue, errors, values, ...formik } = useFormik({
		onSubmit: (_values) => finish(_values.debtItems),
		initialValues: {
			debtItems: debtEntries.map((item) => ({
				...item,
			})),
		},
		validate: (_values) =>
			_values.debtItems
				.filter((item) => !isValidItem(item))
				.reduce((acc, next) => ({ ...acc, [next.id]: 'Values missing' }), {}),
	})

	const currentItem = values.debtItems[currentPage]
	const totalPages = values.debtItems.length

	const { title, subTitle, primaryButtonTitle } = useDebtOverviewTitle(
		currentPage,
		totalPages,
		navigatedFromSummary,
		config.clientName,
		currentItem?.context === 'USER_IS_ADDING',
	)

	const setItemValue = useCallback(
		function <T>(id: string, propertyPath?: string, value?: T): void {
			const item = values.debtItems.find((i) => i.id === id)
			if (!item) return

			const path = [`debtItems[${values.debtItems.indexOf(item)}]`]
			propertyPath && path.push(propertyPath)
			setFieldValue(path.join('.'), value)
		},
		[setFieldValue, values],
	)

	const gotoNextPage = useCallback(() => {
		currentItem?.context === 'USER_IS_ADDING' &&
			setItemValue(currentItem.id, 'context', 'CREATED_BY_USER')
		setCurrentPage((c) => c + 1)
	}, [currentItem, setItemValue])

	const assignPaymentToDebt = useCallback(
		(
			paymentId?: string,
			debtId?: string,
			monthioCreditorId?: number | null,
		) => {
			if (!debtId) return

			if (paymentId === undefined) {
				return setItemValue(debtId, 'debtPayment', {
					id: `DPR_ABU_${generateGuid()}`,
					currency,
					paymentAmount: null,
					frequency: 'monthly',
					monthioCreditorId: monthioCreditorId ?? null,
				})
			}

			const debtPayment = debtPayments.find(
				(payment) => payment.id === paymentId,
			)
			if (!debtPayment) return

			setItemValue(debtId, 'debtPayment', debtPayment)

			const debtItem = values.debtItems.find(
				(_debtItem) => _debtItem.id === debtId,
			)

			if (!debtItem) return
			debtItem.debtPayment = debtPayment
		},
		[debtPayments, setItemValue, values.debtItems, currency],
	)

	const removePaymentFromDebt = useCallback(
		(debtId?: string, monthioCreditorId?: number) => {
			if (!debtId) return

			setItemValue(debtId, 'debtPayment', {
				id: `DPR_ABU_${generateGuid()}`,
				currency,
				paymentAmount: null,
				frequency: 'monthly',
				monthioCreditorId: monthioCreditorId ?? null,
			})
		},
		[setItemValue, currency],
	)

	const updateDebtItem = useCallback<UpdateDebtItem>(
		(id, creditorId, { type, value, debtRecordIndex, paymentId }) => {
			assignPaymentToDebt(paymentId, id, creditorId)
			switch (type) {
				case 'installment':
					return setItemValue(id, 'debtPayment.paymentAmount', value)
				case 'outstanding':
					return setItemValue(
						id,
						`debtRecords[${debtRecordIndex}].principalAmount`,
						value,
					)
				case 'debtType':
					setItemValue(id, 'debtRecords[0].debtType', value)
			}
		},
		[setItemValue, assignPaymentToDebt],
	)

	const deleteDebtItem = useCallback<DeleteDebtItem>(
		(id, value) => {
			removePaymentFromDebt(id)
			switch (value) {
				case 'otherOwner':
				case 'paid':
				case 'unrecognized':
					if (navigatedFromSummary) {
						setItemValue(id, 'debtItems[0].status', value)
					} else {
						gotoNextPage()
						setTimeout(
							() => setItemValue(id, 'debtRecords[0].status', value),
							transitionDurationMs,
						)
					}
					return
				case 'remove':
					return setFieldValue(
						'debtItems',
						values.debtItems.filter((i) => i.id !== id),
					)
				case 'un-delete':
					return setItemValue(id, 'debtRecords[0].status', 'active')
				default:
					return setItemValue(id, 'debtRecords[0].status')
			}
		},
		[
			setItemValue,
			navigatedFromSummary,
			setFieldValue,
			values.debtItems,
			gotoNextPage,
			removePaymentFromDebt,
		],
	)

	const addDebtItem = useCallback(() => {
		const debtSetDebtId = `DR_ABU_${generateGuid()}`
		const debtSetPaymentId = `DPR_ABU_${generateGuid()}`
		const value: DebtItem = {
			id: debtSetDebtId,
			context: 'USER_IS_ADDING',
			debtRecords: [
				{
					currency,
					id: debtSetDebtId,
					debtType: DebtRecordRevampedType.Unknown,
					status: 'active',
					paymentId: debtSetPaymentId,
					interestAmount: null,
					principalAmount: null,
					monthioCreditorId: null,
					loanStartDate: null,
					description: null,
					realEstateId: null,
					addedByApplicant: true,
				},
			],
			debtPayment: {
				id: debtSetPaymentId,
				debtRecordId: debtSetDebtId,
				currency,
				paymentAmount: null,
				frequency: 'monthly',
				monthioCreditorId: null,
			},
		}
		setFieldValue('debtItems', [...values.debtItems, value])
	}, [currency, setFieldValue, values.debtItems])

	const canNavigate =
		currentPage === introPage ||
		(currentPage === totalPages && Object.values(errors || {}).length === 0) ||
		(currentItem != null && isValidItem(currentItem))

	const goBack = useCallback(() => {
		if (currentItem?.context === 'USER_IS_ADDING') {
			deleteDebtItem(currentItem.id, 'remove')
		} else {
			setCurrentPage((c) => c - 1)
		}
	}, [currentItem, deleteDebtItem])

	const goToItem = useCallback(
		(id: string) => {
			const index = values.debtItems.findIndex((x) => x.id === id)
			if (index >= 0) {
				setCurrentPage(index)
				setNavigatedFromSummary(true)
			}
		},
		[values],
	)

	const goToSummary = useCallback(() => {
		setCurrentPage(totalPages)
		setNavigatedFromSummary(false)
	}, [totalPages])

	const onPrimaryButtonClicked = useCallback(() => {
		if (currentPage === totalPages) {
			trackingService.trackEvent(
				'debtOverviewCompleted',
				getMixPanelStatistics(debtEntries, values.debtItems),
			)
			formik.submitForm()
			return
		}
		if (navigatedFromSummary) {
			goToSummary()
			return
		}
		gotoNextPage()
	}, [
		currentPage,
		formik,
		goToSummary,
		gotoNextPage,
		navigatedFromSummary,
		totalPages,
		values,
		debtEntries,
	])

	const updateSuggestedDebtPayment = useCallback(
		(debtPaymentId: string, updatedDebtPayment: DebtPayment) => {
			setSuggestedDebtPayments((prev) =>
				prev.map((payment) =>
					payment.id === debtPaymentId
						? { ...payment, ...updatedDebtPayment }
						: payment,
				),
			)
		},
		[],
	)

	const debtItemPages = useMemo(
		() =>
			Object.fromEntries(
				values.debtItems.map((debt) => [
					debt.id,
					<DebtOverviewCard
						key={debt.id}
						debtItem={debt}
						updateItem={updateDebtItem}
						deleteItem={deleteDebtItem}
						suggestedDebtPayments={suggestedDebtPayments.filter(
							(debtPayment) =>
								!debtEntries.some(
									(_debt) => _debt.debtPayment.id === debtPayment.id,
								) &&
								debtPayment.monthioCreditorId ===
									canonicalDebtRecord(debt).monthioCreditorId &&
								debtPayment.id !== debt.id,
						)}
						updateSuggestedDebtPayment={updateSuggestedDebtPayment}
					/>,
				]),
			),
		[
			debtEntries,
			deleteDebtItem,
			suggestedDebtPayments,
			updateDebtItem,
			updateSuggestedDebtPayment,
			values.debtItems,
		],
	)
	const { t } = useTranslation()

	return (
		<Form
			onSubmit={(e) => {
				e.preventDefault()
				formik.submitForm()
			}}
		>
			<FlowPage>
				<FlowPage.Content $spacing="xl">
					<FlowPage.Header
						title={title}
						description={subTitle}
						slot={
							currentPage === introPage ? <OverviewIllustration /> : undefined
						}
					/>
					<Conditionally condition={currentPage === introPage}>
						<DebtOverviewIntro />
					</Conditionally>
					<Conditionally
						condition={
							currentPage > introPage &&
							currentPage < totalPages &&
							currentItem?.id !== undefined
						}
					>
						{currentItem?.id ? debtItemPages[currentItem.id] : null}
					</Conditionally>
					<Conditionally condition={currentPage === totalPages}>
						<DebtSummary
							debtItems={values.debtItems}
							goToItem={goToItem}
							onAdd={addDebtItem}
						/>
					</Conditionally>
				</FlowPage.Content>
				<FlowPage.Footer
					backButton={
						currentPage > introPage && !navigatedFromSummary && hasIntroPage ? (
							<FlowPage.BackButton onClick={goBack} ariaLabel={t('btn-back')} />
						) : null
					}
					actions={
						<FlowPage.Cta
							disabled={!canNavigate}
							onClick={onPrimaryButtonClicked}
						>
							{primaryButtonTitle}
						</FlowPage.Cta>
					}
				/>
			</FlowPage>
		</Form>
	)
}
